import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { api, BracketStage, Match } from '../api/client';
import { useAsync, Async } from '../components/Async';
import { Flag } from '../components/TeamBadge';
import { ScorersList } from '../components/ScorersList';
import { teamName } from '../lib/flags';
import { H2, Muted } from '../components/ui';

const Podium = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.2fr 1fr;
  gap: 8px;
  align-items: end;
  margin: 8px 0 18px;
`;

const Step = styled.div<{ $h: number; $medal: string }>`
  background: ${({ theme }) => theme.colors.surface};
  border: 2px solid ${({ $medal }) => $medal};
  border-radius: 12px 12px 0 0;
  padding: 12px 6px;
  min-height: ${({ $h }) => $h}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  text-align: center;
  .m { font-size: 1.6rem; }
  .nm { font-weight: 700; font-size: 0.9rem; line-height: 1.1; }
`;

const Pending = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.accent}, #0a3d2c);
  color: #fff;
  border-radius: ${({ theme }) => theme.radius};
  padding: 18px;
  text-align: center;
  margin-bottom: 16px;
  .big { font-size: 1.2rem; font-weight: 800; margin-bottom: 4px; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
`;

const Kpi = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1.5px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius};
  padding: 14px;
  .n { font-size: 1.7rem; font-weight: 800; color: ${({ theme }) => theme.colors.accent}; }
  .l { font-size: 0.82rem; color: ${({ theme }) => theme.colors.textMuted}; }
`;

const SeeAll = styled.button`
  width: 100%;
  text-align: center;
  padding: 10px;
  margin: 4px 0 8px;
  font-weight: 700;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.accent};
`;

function teamOf(t: { resolved: string | null; name: string }): string {
  return t.resolved ?? t.name;
}

// Podio desde los resultados REALES de la final (104) y el 3er puesto (103).
function podium(stages: BracketStage[]): { champ: string; runner: string; third: string | null } | null {
  const all = stages.flatMap((s) => s.matches);
  const final = all.find((m) => m.stage?.key === 'final');
  const third = all.find((m) => m.stage?.key === 'third');
  if (!final || !final.score) return null;
  const [a, b] = final.score.ft;
  if (a === b) return null;
  const champ = a > b ? teamOf(final.team1) : teamOf(final.team2);
  const runner = a > b ? teamOf(final.team2) : teamOf(final.team1);
  let thirdTeam: string | null = null;
  if (third && third.score && third.score.ft[0] !== third.score.ft[1]) {
    thirdTeam = third.score.ft[0] > third.score.ft[1] ? teamOf(third.team1) : teamOf(third.team2);
  }
  return { champ, runner, third: thirdTeam };
}

function finalLabel(stages: BracketStage[]): Match | null {
  return stages.flatMap((s) => s.matches).find((m) => m.stage?.key === 'final') ?? null;
}

export function Resumen() {
  const navigate = useNavigate();
  const stats = useAsync(() => api.stats(), [], '/stats');
  const scorers = useAsync(() => api.scorers(), [], '/scorers');
  const bracket = useAsync(() => api.bracket(), [], '/bracket');

  return (
    <>
      <h2 style={{ marginTop: 4 }}>🏅 Resumen del torneo</h2>

      <Async state={bracket}>
        {(d) => {
          const p = podium(d.stages);
          if (p) {
            return (
              <Podium>
                <Step $h={120} $medal="#c0c0c0">
                  <span className="m">🥈</span>
                  <Flag team={p.runner} size={26} />
                  <span className="nm">{teamName(p.runner)}</span>
                </Step>
                <Step $h={150} $medal="#f4b324">
                  <span className="m">🥇</span>
                  <Flag team={p.champ} size={34} />
                  <span className="nm">{teamName(p.champ)}</span>
                </Step>
                <Step $h={100} $medal="#cd7f32">
                  <span className="m">🥉</span>
                  {p.third && <Flag team={p.third} size={24} />}
                  <span className="nm">{p.third ? teamName(p.third) : '—'}</span>
                </Step>
              </Podium>
            );
          }
          const fin = finalLabel(d.stages);
          return (
            <Pending>
              <div className="big">🏆 El campeón se define en la final</div>
              {fin && (
                <div>
                  {teamName(teamOf(fin.team1))} vs {teamName(teamOf(fin.team2))}
                </div>
              )}
            </Pending>
          );
        }}
      </Async>

      <Async state={stats}>
        {({ stats: s }) => (
          <Grid>
            <Kpi><div className="n">{s.goals}</div><div className="l">goles</div></Kpi>
            <Kpi><div className="n">{s.avg}</div><div className="l">por partido</div></Kpi>
            <Kpi><div className="n">{s.played}</div><div className="l">partidos jugados</div></Kpi>
            <Kpi><div className="n">{s.penalties}</div><div className="l">goles de penal</div></Kpi>
          </Grid>
        )}
      </Async>

      <H2>🥇 Goleadores</H2>
      <Async state={scorers}>
        {(d) => (
          <>
            <ScorersList scorers={d.scorers.slice(0, 5)} />
            <SeeAll onClick={() => navigate('/scorers')}>Ver ranking completo →</SeeAll>
          </>
        )}
      </Async>

      <SeeAll onClick={() => navigate('/stats')}>📊 Ver todas las estadísticas →</SeeAll>
      <Muted style={{ textAlign: 'center', margin: '14px 4px 4px', fontSize: '0.92rem' }}>
        ℹ️ Versión gratuita: los datos se actualizan ~2 horas después de cada partido.
      </Muted>
    </>
  );
}
